/*
 * Super Admin — Product Catalog (mini-CMS) + Pending Signups approval queue.
 * Single-file combined view, two tabs.
 *
 * Mounted at /app/products from AppArea (added separately).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { CheckCircle2, Clock, XCircle, Pencil, Save, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const adminHdrs = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id":   session?.user?.id   || "",
  "x-user-role": session?.user?.role || "super_admin",
});

/* ── Product Catalog tab ── */
export const ProductCatalog = ({ session, productType = null, variant = "full", defaultOpen = false }) => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/products`, { headers: adminHdrs(session) });
      setProducts(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open the single card on a focused (split) product view.
  useEffect(() => {
    if (defaultOpen && !editing && products.length) {
      const list = productType ? products.filter((p) => p.product_type === productType) : products;
      if (list.length === 1) setEditing(list[0].id);
    }
  }, [products]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (id, patch) => {
    try {
      const r = await axios.patch(`${API}/api/admin/products/${id}`, patch, { headers: adminHdrs(session) });
      setProducts((arr) => arr.map((p) => (p.id === id ? r.data : p)));
      toast.success("Product updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  if (loading) return <div className="p-8 text-[#6b7280]">Loading products…</div>;

  const shown = productType ? products.filter((p) => p.product_type === productType) : products;

  return (
    <div className="space-y-4" data-testid="admin-product-catalog">
      {shown.length === 0 && <div className="p-8 text-[#6b7280]">No product found.</div>}
      {shown.map((p) => (
        <ProductCard key={p.id} p={p} variant={variant} isOpen={editing === p.id} onToggle={() => setEditing(editing === p.id ? null : p.id)} onSave={(patch) => save(p.id, patch)} />
      ))}
    </div>
  );
};

const ProductCard = ({ p, isOpen, onToggle, onSave, variant = "full" }) => {
  const [draft, setDraft] = useState(p);
  useEffect(() => { setDraft(p); }, [p]);

  const updateField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updateSection = (k, v) => setDraft((d) => ({ ...d, section_content: { ...(d.section_content || {}), [k]: v } }));

  const showSettings = variant === "full" || variant === "settings";
  const showLanding = variant === "full" || variant === "landing";
  const showFaq = variant === "full" || variant === "faq";
  const showExperts = variant === "full";

  const saveAll = () => {
    onSave({
      is_active: draft.is_active,
      headline: draft.headline,
      subheadline: draft.subheadline,
      price: Number(draft.price),
      section_content: draft.section_content,
      faq_content: draft.faq_content,
      seo_title: draft.seo_title,
      seo_meta_description: draft.seo_meta_description,
      internal_notification_email: draft.internal_notification_email,
      weekly_reminders_enabled: draft.weekly_reminders_enabled,
      enabled_payment_methods: draft.enabled_payment_methods,
    });
  };

  return (
    <div className="rounded-xl border border-[#e5e7eb] bg-white" data-testid={`product-card-${p.product_type}`}>
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left">
        <div>
          <div className="text-[11.5px] uppercase tracking-wider text-[#6b7280] font-semibold">{p.product_type}</div>
          <div className="text-[17px] font-bold text-[#111827]">{p.product_name}</div>
          <div className="text-[12px] text-[#6b7280] mt-1">
            {p.preview_route} · {p.default_tenant_type} · ${(p.price || 0).toLocaleString()} ·{" "}
            <span className={p.is_active ? "text-[#059669]" : "text-[#b91c1c]"}>{p.is_active ? "Active" : "Inactive"}</span>
          </div>
        </div>
        {isOpen ? <ChevronUp className="h-5 w-5 text-[#6b7280]" /> : <ChevronDown className="h-5 w-5 text-[#6b7280]" />}
      </button>

      {isOpen && (
        <div className="border-t border-[#e5e7eb] p-5 space-y-4">
          {showSettings && (
            <>
              <ToggleRow label="Product active" value={!!draft.is_active} onChange={(v) => updateField("is_active", v)} />
              <Field label="Price (USD)" value={String(draft.price ?? 0)} onChange={(v) => updateField("price", v)} type="number" />
              <Field label="Internal Notification Email" value={draft.internal_notification_email || ""} onChange={(v) => updateField("internal_notification_email", v)} />
              <ToggleRow label="Weekly reminders enabled" value={!!draft.weekly_reminders_enabled} onChange={(v) => updateField("weekly_reminders_enabled", v)} />
            </>
          )}

          {variant === "settings" && p.product_type === "caporilli_diagnostic" && (
            <a
              href="/app/myschoolrx/cms#landing"
              className="block rounded-lg border border-[#bfe3ee] bg-[#f0f9fc] px-4 py-3 text-[13px] text-[#00485f] hover:bg-[#e6f4f8] transition-colors"
              data-testid="product-settings-cms-link"
            >
              <span className="font-semibold">Landing copy, FAQ and Experts moved to Website CMS →</span>
            </a>
          )}

          {showLanding && (
            <>
              <Field label="Headline" value={draft.headline || ""} onChange={(v) => updateField("headline", v)} />
              <Field label="Subheadline" value={draft.subheadline || ""} onChange={(v) => updateField("subheadline", v)} multiline />
              <Field label="Primary CTA Label" value={draft.section_content?.primary_cta_label || ""} onChange={(v) => updateSection("primary_cta_label", v)} />
              <Field label="Secondary CTA Label" value={draft.section_content?.secondary_cta_label || ""} onChange={(v) => updateSection("secondary_cta_label", v)} />
              <Field label="Supporting Line" value={draft.section_content?.supporting_line || ""} onChange={(v) => updateSection("supporting_line", v)} multiline />
              <Field label="Hero background image URL" value={draft.section_content?.hero_bg_image || ""} onChange={(v) => updateSection("hero_bg_image", v)} />
              <Field label="Hero photo visibility % (0 = hidden, ~22 default)" type="number" value={draft.section_content?.hero_bg_opacity ?? 22} onChange={(v) => updateSection("hero_bg_opacity", v === "" ? "" : Math.max(0, Math.min(100, parseInt(v, 10) || 0)))} />
              <Field label="Hero photo focal point (center, right, left, top, bottom, or e.g. 70% 30%)" value={draft.section_content?.hero_bg_position || ""} onChange={(v) => updateSection("hero_bg_position", v)} />
              <Field label="Problem Section Intro" value={draft.section_content?.problem_section_intro || ""} onChange={(v) => updateSection("problem_section_intro", v)} multiline />
              <Field label="SEO Title" value={draft.seo_title || ""} onChange={(v) => updateField("seo_title", v)} />
              <Field label="SEO Description" value={draft.seo_meta_description || ""} onChange={(v) => updateField("seo_meta_description", v)} multiline />
            </>
          )}

          {showFaq && (
            <>
              <FAQEditor faqs={draft.faq_content || []} onChange={(faqs) => updateField("faq_content", faqs)} />
              <Field
                label="FAQs shown before 'More questions' (landing page)"
                type="number"
                value={draft.section_content?.faq_default_count ?? 6}
                onChange={(v) => updateSection("faq_default_count", v === "" ? "" : Math.max(1, parseInt(v, 10) || 6))}
              />
            </>
          )}

          {/* Experts CMS — caporilli_diagnostic only, full catalog view only */}
          {showExperts && p.product_type === "caporilli_diagnostic" && (
            <ExpertsEditor
              experts={draft.section_content?.experts || {}}
              onChange={(experts) => updateSection("experts", experts)}
            />
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={saveAll} className="bg-[#0b1220] hover:bg-black text-white" data-testid={`product-save-${p.product_type}`}>
              <Save className="h-4 w-4 mr-1.5" /> Save changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, multiline, type = "text" }) => (
  <div>
    <label className="block text-[11.5px] font-semibold uppercase tracking-wider text-[#6b7280] mb-1.5">{label}</label>
    {multiline ? (
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} className="bg-white border-[#e5e7eb] text-[#111827] text-[13px] min-h-[60px]" />
    ) : (
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="bg-white border-[#e5e7eb] text-[#111827] text-[13px] h-10" />
    )}
  </div>
);

const ToggleRow = ({ label, value, onChange }) => (
  <div className="flex items-center justify-between rounded-lg border border-[#e5e7eb] bg-[#f9fafb] px-4 py-2.5">
    <span className="text-[13px] font-semibold text-[#374151]">{label}</span>
    <button onClick={() => onChange(!value)} className={`h-6 w-11 rounded-full transition-colors ${value ? "bg-[#007a9e]" : "bg-[#d1d5db]"} relative`}>
      <span className={`absolute top-0.5 ${value ? "right-0.5" : "left-0.5"} h-5 w-5 rounded-full bg-white transition-all`} />
    </button>
  </div>
);

const FAQEditor = ({ faqs, onChange }) => {
  const update = (i, k, v) => onChange(faqs.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)));
  const add = () => onChange([...faqs, { q: "New question", a: "" }]);
  const remove = (i) => onChange(faqs.filter((_, idx) => idx !== i));
  return (
    <div>
      <label className="block text-[11.5px] font-semibold uppercase tracking-wider text-[#6b7280] mb-2">FAQ entries ({faqs.length})</label>
      <div className="space-y-2">
        {faqs.map((f, i) => (
          <div key={i} className="rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-3 space-y-2">
            <Input value={f.q} onChange={(e) => update(i, "q", e.target.value)} className="bg-white border-[#e5e7eb] text-[13px] h-9" />
            <Textarea value={f.a} onChange={(e) => update(i, "a", e.target.value)} className="bg-white border-[#e5e7eb] text-[13px] min-h-[50px]" />
            <button onClick={() => remove(i)} className="text-[11.5px] text-[#b91c1c] hover:underline">Remove</button>
          </div>
        ))}
        <button onClick={add} className="text-[12px] font-semibold text-[#1a56db] hover:underline">+ Add FAQ</button>
      </div>
    </div>
  );
};

/* ── Experts CMS editor (caporilli_diagnostic only) ──
   Edits the section_content.experts block on the product. Supports:
   • page-level fields (headline, subheadline, bottom CTAs, roster note)
   • per-expert cards (name, title, organization, bio, photo URL,
     credentials[], expertise[], experience, order, active toggle)
   • adding new experts, reordering via order field, soft-delete via active. */
const ExpertPhotoUpload = ({ onUploaded, testid }) => {
  const [busy, setBusy] = useState(false);
  const ref = React.useRef(null);
  const pick = async (ev) => {
    const f = ev.target.files?.[0]; ev.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { alert("Image must be under 5 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await fetch(`${API}/api/assets/expert-image`, {
        method: "POST", headers: { "x-user-role": "admin", "x-user-id": "dirk" }, body: fd });
      if (!r.ok) throw new Error("Upload failed");
      const d = await r.json();
      onUploaded(d.url);
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pick} data-testid={`${testid}-input`} />
      <button onClick={() => ref.current?.click()} disabled={busy}
        className="h-10 px-3 rounded border border-[#e5e7eb] bg-white text-[12px] font-medium text-[#374151] hover:bg-[#f9fafb] whitespace-nowrap disabled:opacity-50" data-testid={testid}>
        {busy ? "Uploading…" : "Upload Image"}
      </button>
    </>
  );
};

const ExpertsEditor = ({ experts, onChange }) => {
  const list = Array.isArray(experts?.list) ? experts.list : [];

  const updatePage = (k, v) => onChange({ ...experts, [k]: v, list });
  const updateExpert = (idx, k, v) => onChange({
    ...experts,
    list: list.map((e, i) => (i === idx ? { ...e, [k]: v } : e)),
  });
  const updateArr = (idx, k, v) => updateExpert(idx, k, (v || "").split("\n").map((s) => s.trim()).filter(Boolean));
  const addExpert = () => onChange({
    ...experts,
    list: [...list, {
      id: `expert_${Date.now().toString(36)}`,
      name: "New Expert",
      title: "Title",
      organization: "",
      bio: "",
      expertise: [],
      credentials: [],
      experience: "",
      photo: "",
      initials: "NE",
      color: "#3B4FE4",
      order: list.length + 1,
      active: true,
    }],
  });
  const removeExpert = (idx) => onChange({ ...experts, list: list.filter((_, i) => i !== idx) });
  const move = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= list.length) return;
    const next = [...list];
    [next[idx], next[ni]] = [next[ni], next[idx]];
    onChange({ ...experts, list: next.map((e, i) => ({ ...e, order: i + 1 })) });
  };

  return (
    <div className="rounded-xl border border-[#bfe3ee] bg-[#f0f9fc] p-4 space-y-4" data-testid="admin-experts-editor">
      <div>
        <div className="text-[11.5px] uppercase tracking-wider text-[#00485f] font-bold">Experts page (CMS)</div>
        <div className="text-[12px] text-[#00485f]/80">Edits here render on /myschoolrx/experts.</div>
      </div>

      <div className="space-y-3">
        <Field label="Experts page headline" value={experts.headline || ""} onChange={(v) => updatePage("headline", v)} />
        <Field label="Experts page subheadline / intro" value={experts.subheadline || ""} onChange={(v) => updatePage("subheadline", v)} multiline />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Primary CTA label" value={experts.cta_label || ""} onChange={(v) => updatePage("cta_label", v)} />
          <Field label="Primary CTA destination" value={experts.cta_destination || ""} onChange={(v) => updatePage("cta_destination", v)} />
        </div>
        <Field label="Roster note (below cards)" value={experts.roster_note || ""} onChange={(v) => updatePage("roster_note", v)} />
        <Field label="Bottom CTA heading" value={experts.bottom_cta_heading || ""} onChange={(v) => updatePage("bottom_cta_heading", v)} />
        <Field label="Bottom CTA body" value={experts.bottom_cta_body || ""} onChange={(v) => updatePage("bottom_cta_body", v)} multiline />
      </div>

      <div className="pt-2 border-t border-[#bfe3ee]">
        <div className="text-[11.5px] uppercase tracking-wider text-[#00485f] font-bold mb-2">Expert profile cards ({list.length})</div>
        <div className="space-y-3">
          {list.map((e, idx) => (
            <div key={e.id || idx} className="rounded-lg border border-[#e5e7eb] bg-white p-3 space-y-2" data-testid={`admin-expert-card-${e.id || idx}`}>
              <div className="flex items-start gap-3 flex-wrap">
                {e.photo && <img src={e.photo} alt="" className="h-14 w-14 rounded-full object-cover border border-[#e5e7eb]" />}
                <div className="flex-1 min-w-[200px]">
                  <Input value={e.name || ""} onChange={(ev) => updateExpert(idx, "name", ev.target.value)} placeholder="Name" className="bg-white border-[#e5e7eb] text-[13px] h-9 font-semibold" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    <Input value={e.title || ""} onChange={(ev) => updateExpert(idx, "title", ev.target.value)} placeholder="Title" className="bg-white border-[#e5e7eb] text-[12px] h-9" />
                    <Input value={e.organization || ""} onChange={(ev) => updateExpert(idx, "organization", ev.target.value)} placeholder="Organization" className="bg-white border-[#e5e7eb] text-[12px] h-9" />
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[11.5px] px-2 py-0.5 rounded border border-[#e5e7eb] disabled:opacity-30" data-testid={`admin-expert-up-${idx}`}>↑</button>
                    <button onClick={() => move(idx, 1)} disabled={idx === list.length - 1} className="text-[11.5px] px-2 py-0.5 rounded border border-[#e5e7eb] disabled:opacity-30" data-testid={`admin-expert-down-${idx}`}>↓</button>
                  </div>
                  <ToggleRow label="Active" value={e.active !== false} onChange={(v) => updateExpert(idx, "active", v)} />
                </div>
              </div>

              <Field label="Bio" value={e.bio || ""} onChange={(v) => updateExpert(idx, "bio", v)} multiline />
              <div className="flex items-end gap-2">
                <div className="flex-1"><Field label="Photo / Image URL" value={e.photo || ""} onChange={(v) => updateExpert(idx, "photo", v)} /></div>
                <ExpertPhotoUpload onUploaded={(url) => updateExpert(idx, "photo", url)} testid={`expert-photo-upload-${idx}`} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Field label="Credentials (one per line)" value={(e.credentials || []).join("\n")} onChange={(v) => updateArr(idx, "credentials", v)} multiline />
                <Field label="Areas of expertise (one per line)" value={(e.expertise || []).join("\n")} onChange={(v) => updateArr(idx, "expertise", v)} multiline />
              </div>
              <Field label="Experience" value={e.experience || ""} onChange={(v) => updateExpert(idx, "experience", v)} multiline />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Field label="Initials (fallback)" value={e.initials || ""} onChange={(v) => updateExpert(idx, "initials", v)} />
                <Field label="Accent color" value={e.color || ""} onChange={(v) => updateExpert(idx, "color", v)} />
                <Field label="Display order" value={String(e.order ?? idx + 1)} onChange={(v) => updateExpert(idx, "order", Number(v) || 0)} type="number" />
              </div>
              <div className="pt-1 flex justify-end">
                <button onClick={() => removeExpert(idx)} className="text-[11.5px] text-[#b91c1c] hover:underline" data-testid={`admin-expert-remove-${idx}`}>Remove expert</button>
              </div>
            </div>
          ))}
          <button onClick={addExpert} className="text-[13px] font-semibold text-[#1a56db] hover:underline" data-testid="admin-expert-add">+ Add expert</button>
        </div>
      </div>
    </div>
  );
};

/* ── Pending Signups tab ── */
export const PendingSignups = ({ session }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/pending-signups`, { headers: adminHdrs(session) });
      setRows(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (id) => {
    try {
      const r = await axios.post(`${API}/api/admin/pending-signups/${id}/approve`, { notes: "" }, { headers: adminHdrs(session) });
      const email = r?.data?.login_email;
      const pwd = r?.data?.temp_password;
      if (email && pwd) {
        toast.success(
          <div className="flex flex-col gap-1.5">
            <span className="font-semibold">Tenant provisioned</span>
            <div className="text-[12px]">Login URL: <a href="/diagnostic/login" target="_blank" rel="noreferrer" className="text-[#1a56db] underline">/diagnostic/login</a></div>
            <div className="text-[12px]">Email: <code className="bg-black/10 px-1.5 py-0.5 rounded">{email}</code></div>
            <div className="text-[12px]">Temp password: <code className="bg-black/10 px-1.5 py-0.5 rounded">{pwd}</code></div>
            <button
              onClick={() => navigator.clipboard?.writeText(`Login: ${window.location.origin}/diagnostic/login\nEmail: ${email}\nTemp password: ${pwd}`)}
              className="text-[11.5px] text-[#374151] underline text-left mt-1"
              data-testid={`approve-copy-creds-${id}`}
            >
              Copy login details
            </button>
          </div>,
          { duration: 30000 }
        );
      } else {
        toast.success("Tenant provisioned");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const cancel = async (id) => {
    try {
      await axios.post(`${API}/api/admin/pending-signups/${id}/cancel`, {}, { headers: adminHdrs(session) });
      toast.success("Signup cancelled");
      load();
    } catch {
      toast.error("Cancel failed");
    }
  };

  const STATUS = {
    po_pending_review: { label: "PO Pending Review", color: "text-[#a16207] bg-[#fefce8] border-[#fde047]", icon: Clock },
    tenant_created:    { label: "Tenant Created",     color: "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]", icon: CheckCircle2 },
    cancelled:         { label: "Cancelled",          color: "text-[#6b7280] bg-[#f3f4f6] border-[#e5e7eb]", icon: XCircle },
  };

  if (loading) return <div className="p-8 text-[#6b7280]">Loading pending signups…</div>;
  if (rows.length === 0) return <div className="p-8 text-center text-[#6b7280]">No pending signups yet.</div>;

  return (
    <div className="space-y-3" data-testid="admin-pending-signups">
      {rows.map((s) => {
        const meta = STATUS[s.signup_status] || STATUS.po_pending_review;
        const Icon = meta.icon;
        return (
          <div key={s.id} className="rounded-xl border border-[#e5e7eb] bg-white p-4 flex items-start gap-4 flex-wrap" data-testid={`pending-${s.id}`}>
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2">
                <span className={`text-[11.5px] font-semibold uppercase tracking-wider px-2 py-1 rounded border ${meta.color} inline-flex items-center gap-1`}>
                  <Icon className="h-3 w-3" /> {meta.label}
                </span>
                <span className="text-[11.5px] text-[#6b7280]">{new Date(s.created_at).toLocaleString()}</span>
              </div>
              <div className="mt-2 text-[15px] font-bold text-[#111827]">{s.school_name}</div>
              <div className="text-[13px] text-[#374151]">{s.first_name} {s.last_name} · {s.role_title || "—"}</div>
              <div className="text-[12px] text-[#6b7280] mt-1">{s.email} · {s.phone || "no phone"} · {s.state || "—"}</div>
              {s.school_website && <div className="text-[12px] text-[#1a56db] mt-1">{s.school_website}</div>}
              {s.ai_back_office_readiness_label && (
                <div className="mt-2 inline-flex items-start gap-1.5 text-[11.5px] text-[#00485f] bg-[#f0f9fc] border border-[#bfe3ee] px-2 py-1 rounded" data-testid={`signup-ai-response-${s.id}`}>
                  <span className="font-bold uppercase tracking-wider text-[11.5px]">AI back-office:</span>
                  <span className="font-medium">{s.ai_back_office_readiness_label}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              {s.signup_status === "po_pending_review" && (
                <>
                  <Button onClick={() => approve(s.id)} className="bg-[#059669] hover:bg-[#047857] text-white text-[13px]" data-testid={`approve-${s.id}`}>
                    Approve & Create Tenant
                  </Button>
                  <Button onClick={() => cancel(s.id)} variant="outline" className="border-[#e5e7eb] text-[#111827] text-[13px]" data-testid={`cancel-${s.id}`}>
                    Cancel
                  </Button>
                </>
              )}
              {s.tenant_id && <div className="text-[11.5px] text-[#6b7280] font-mono">tenant: {s.tenant_id.slice(0, 24)}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ── Top-level page with tabs ── */
const ProductsAdminPage = ({ session }) => {
  const [tab, setTab] = useState("catalog");
  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="products-admin">
      <h1 className="text-2xl font-bold text-[#111827]">Product Catalog</h1>
      <p className="text-sm text-[#6b7280] mt-1">Configure landing copy, pricing, and FAQs. Approve pending signups to provision tenants.</p>

      <div className="mt-6 inline-flex rounded-lg border border-[#e5e7eb] bg-white p-1">
        {[
          { k: "catalog",  l: "Products" },
          { k: "pending",  l: "Pending Signups" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${tab === t.k ? "bg-[#0b1220] text-white" : "text-[#374151] hover:bg-[#f9fafb]"}`}
            data-testid={`admin-tab-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "catalog" && <ProductCatalog session={session} />}
        {tab === "pending" && <PendingSignups session={session} />}
      </div>
    </div>
  );
};

export default ProductsAdminPage;
